import React, { useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, TextArea } from '@ygtechnologies/bygr-frontend-component-library';
import { ExternalAgreement } from '@/types';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { ValidationForm, ValidationScope } from '@/components/forms/validation';
import { Button } from '@/components/Buttons';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { updateExternalAgreement } from '@/services/external-agreements';
import ChangeRequestParticipantSelectList, {
    ChangeRequestParticipantSelectListItem,
} from '@/seller/components/change-requests/ChangeRequestParticipantSelectList';
import InfoIconWithTooltip from '@/seller/components/InfoIconWithTooltip';

export interface ExternalAgreementEditParticipantsDialogProps {
    externalAgreement: ExternalAgreement;
    show: boolean;
    onClose(): void;
    onSave(externalAgreement: ExternalAgreement): void;
}
const ExternalAgreementEditParticipantsDialog: React.FC<ExternalAgreementEditParticipantsDialogProps> = ({
    externalAgreement,
    show,
    onClose,
    onSave,
}) => {
    const [saving, setSaving] = useState(false);
    const [participants, setParticipants] = useState<ChangeRequestParticipantSelectListItem[]>([]);
    const [participantsNote, setParticipantsNote] = useState('');

    const onOpen = () => {
        setParticipants(externalAgreement.participants);
        setParticipantsNote(externalAgreement.participantsNote);
    };
    const handleCancelClick = () => {
        triggerHandler(onClose);
    };

    const handleAcceptClick = async (e) => {
        e.preventDefault();
        setSaving(true);
        var ea = await updateExternalAgreement(externalAgreement.id, {
            participants: participants,
            participantsNote: participantsNote,
        });
        setSaving(false);
        onSave(ea);
    };

    return (
        <Modal
            isOpen={show}
            onOpen={onOpen}
            toggle={handleCancelClick}
            size="lg">
            <ValidationForm onSubmit={handleAcceptClick}>
                <ModalHeader>
                    <Trans>Edit participants</Trans>
                </ModalHeader>
                <ModalBody>
                    <ValidationScope>
                        <div className="form-group">
                            <ChangeRequestParticipantSelectList
                                onChange={(value) => setParticipants(value)}
                                projectId={externalAgreement.projectId}
                                value={participants}
                                forceCurrentUser
                            />
                        </div>

                        {(participantsNote || (participants && participants.length > 1)) && (
                            <div className="form-group">
                                <label>
                                    <Trans>Note for participants</Trans>
                                    <InfoIconWithTooltip
                                        color="light"
                                        title={<Trans>Note for participants</Trans>}
                                        content={
                                            <Trans>
                                                Please note, this text will be visible to everyone who can see this external
                                                agreement, and will be added to the email when the external agreement is
                                                approved.
                                            </Trans>
                                        }
                                    />
                                </label>
                                <TextArea
                                    placeholder={t`Describe the required changes here...`}
                                    required
                                    className="form-control"
                                    autoResize
                                    minRows={4}
                                    value={participantsNote}
                                    onChange={(e) => setParticipantsNote(e.target.value)}
                                />
                            </div>
                        )}
                    </ValidationScope>
                </ModalBody>
                <ModalFooter>
                    <Button color="tertiary" onClick={handleCancelClick} disabled={saving}>
                        <Trans>Cancel</Trans>
                    </Button>
                    <Button type="submit" color="primary" loading={saving}>
                        <Trans>Save</Trans>
                    </Button>
                </ModalFooter>
            </ValidationForm>
        </Modal>
    );
};

export default ExternalAgreementEditParticipantsDialog;